/** Visual predicate tree builder для D1 Promotion rules.
 *
 * Tree-узлы:
 *  - GROUP (and/or/not) — обёртка с N child-узлами
 *  - LEAF — конкретное условие:
 *     - product_in: [ids]
 *     - category_in: [ids]
 *     - min_total: tiyin
 *     - min_quantity: int
 *
 * Output совместим с backend evaluate(): {and:[...]} / {or:[...]} /
 * {not:node} / leaf-объекты.
 *
 * Кнопка «JSON» переключает на raw textarea для power users.
 */

import { ChevronDown, Code2, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// ─── Types ─────────────────────────────────────────────────────────────

export type Predicate = Record<string, unknown>;

type GroupOp = "and" | "or" | "not";
type LeafType = "product_in" | "category_in" | "min_total" | "min_quantity";

const GROUP_OPS: GroupOp[] = ["and", "or", "not"];
const LEAF_TYPES: { value: LeafType; label: string }[] = [
  { value: "product_in", label: "Товары (IDs)" },
  { value: "category_in", label: "Категории (IDs)" },
  { value: "min_total", label: "Минимальная сумма (тийины)" },
  { value: "min_quantity", label: "Минимум товаров" },
];

interface Props {
  value: Predicate;
  onChange: (next: Predicate) => void;
}

// ─── Tree helpers ──────────────────────────────────────────────────────

function isGroup(node: Predicate): GroupOp | null {
  if (typeof node !== "object" || node == null) return null;
  if ("and" in node) return "and";
  if ("or" in node) return "or";
  if ("not" in node) return "not";
  return null;
}

function isLeaf(node: Predicate): LeafType | null {
  if (typeof node !== "object" || node == null) return null;
  for (const lt of LEAF_TYPES) {
    if (lt.value in node) return lt.value;
  }
  return null;
}

function isEmpty(node: Predicate): boolean {
  return typeof node === "object" && node !== null && Object.keys(node).length === 0;
}

function defaultLeaf(type: LeafType): Predicate {
  switch (type) {
    case "product_in":
    case "category_in":
      return { [type]: [] };
    case "min_total":
      return { min_total: 0 };
    case "min_quantity":
      return { min_quantity: 1 };
  }
}

// ─── Component ─────────────────────────────────────────────────────────

export function PredicateBuilder({ value, onChange }: Props): JSX.Element {
  const [rawMode, setRawMode] = useState(false);
  const [rawJson, setRawJson] = useState(() =>
    JSON.stringify(value ?? {}, null, 2),
  );

  // Sync raw → value
  const commitRaw = () => {
    try {
      const parsed = JSON.parse(rawJson || "{}");
      onChange(parsed);
      setRawMode(false);
    } catch {
      // toast?
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Условия применения</Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            if (rawMode) {
              commitRaw();
            } else {
              setRawJson(JSON.stringify(value ?? {}, null, 2));
              setRawMode(true);
            }
          }}
          className="h-7 text-xs"
        >
          <Code2 className="w-3 h-3 mr-1" />
          {rawMode ? "Готово (применить JSON)" : "JSON"}
        </Button>
      </div>

      {rawMode ? (
        <Textarea
          rows={8}
          className="font-mono text-xs"
          value={rawJson}
          onChange={(e) => setRawJson(e.target.value)}
        />
      ) : (
        <div className="border rounded p-2 bg-background">
          <NodeView value={value ?? {}} onChange={onChange} depth={0} />
        </div>
      )}
    </div>
  );
}

interface NodeProps {
  value: Predicate;
  onChange: (next: Predicate) => void;
  /** depth for visual indent + nesting limit */
  depth: number;
  /** для child-узлов group — кнопка удаления */
  onRemove?: () => void;
}

function NodeView({ value, onChange, depth, onRemove }: NodeProps): JSX.Element {
  const groupOp = isGroup(value);
  const leafType = isLeaf(value);

  if (isEmpty(value)) {
    return <EmptyNode onChange={onChange} onRemove={onRemove} />;
  }

  if (groupOp) {
    return (
      <GroupNode
        op={groupOp}
        value={value}
        onChange={onChange}
        depth={depth}
        onRemove={onRemove}
      />
    );
  }

  if (leafType) {
    return (
      <LeafNode
        type={leafType}
        value={value}
        onChange={onChange}
        onRemove={onRemove}
      />
    );
  }

  // Unknown shape — fallback на JSON чтоб не сломаться
  return (
    <div className="text-[10px] font-mono p-2 bg-muted/30 rounded">
      {JSON.stringify(value)}
    </div>
  );
}

function EmptyNode({
  onChange,
  onRemove,
}: {
  onChange: (next: Predicate) => void;
  onRemove?: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground italic">
        Условий нет (применяется ко всем)
      </span>
      <PickerMenu onPick={(picked) => onChange(picked)} />
      {onRemove && (
        <Button size="icon" variant="ghost" onClick={onRemove} className="h-6 w-6">
          <Trash2 className="w-3 h-3 text-destructive" />
        </Button>
      )}
    </div>
  );
}

function PickerMenu({
  onPick,
}: {
  onPick: (picked: Predicate) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        className="h-7 text-xs"
      >
        <Plus className="w-3 h-3 mr-1" />
        Добавить условие
        <ChevronDown className="w-3 h-3 ml-1" />
      </Button>
      {open && (
        <div
          className="absolute left-0 top-full z-10 mt-1 w-52 rounded-md border bg-popover shadow-md text-xs"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="border-b px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Группа
          </div>
          {GROUP_OPS.map((op) => (
            <button
              key={op}
              type="button"
              className="block w-full text-left px-2 py-1.5 hover:bg-accent font-mono"
              onClick={() => {
                onPick(op === "not" ? { not: {} } : { [op]: [] });
                setOpen(false);
              }}
            >
              {op.toUpperCase()}
            </button>
          ))}
          <div className="border-b border-t px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Условие
          </div>
          {LEAF_TYPES.map((lt) => (
            <button
              key={lt.value}
              type="button"
              className="block w-full text-left px-2 py-1.5 hover:bg-accent"
              onClick={() => {
                onPick(defaultLeaf(lt.value));
                setOpen(false);
              }}
            >
              {lt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupNode({
  op,
  value,
  onChange,
  depth,
  onRemove,
}: {
  op: GroupOp;
  value: Predicate;
  onChange: (next: Predicate) => void;
  depth: number;
  onRemove?: () => void;
}): JSX.Element {
  const children: Predicate[] = useMemo(() => {
    if (op === "not") {
      const v = value.not as Predicate;
      return [v ?? {}];
    }
    return (value[op] as Predicate[]) ?? [];
  }, [op, value]);

  const setChildren = (next: Predicate[]) => {
    if (op === "not") {
      onChange({ not: next[0] ?? {} });
    } else {
      onChange({ [op]: next });
    }
  };

  const setOp = (newOp: GroupOp) => {
    if (newOp === op) return;
    if (newOp === "not") {
      onChange({ not: children[0] ?? {} });
    } else {
      onChange({ [newOp]: op === "not" ? children : children });
    }
  };

  const addChild = (picked: Predicate) => {
    if (op === "not") {
      // NOT принимает только 1 child — заменяем
      setChildren([picked]);
    } else {
      setChildren([...children, picked]);
    }
  };

  const updateChild = (idx: number, child: Predicate) => {
    const next = children.slice();
    next[idx] = child;
    setChildren(next);
  };

  const removeChild = (idx: number) => {
    const next = children.filter((_, i) => i !== idx);
    setChildren(next);
  };

  const colorByOp: Record<GroupOp, string> = {
    and: "border-blue-300 bg-blue-50/30",
    or: "border-amber-300 bg-amber-50/30",
    not: "border-red-300 bg-red-50/30",
  };

  return (
    <div
      className={cn(
        "border-l-2 rounded-r pl-2 py-1 space-y-1",
        colorByOp[op],
        depth > 0 && "ml-2",
      )}
    >
      <div className="flex items-center gap-1.5">
        <Select value={op} onValueChange={(v: GroupOp) => setOp(v)}>
          <SelectTrigger className="h-6 w-20 text-xs font-mono">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_OPS.map((o) => (
              <SelectItem key={o} value={o} className="font-mono text-xs">
                {o.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground">
          ({children.length} {op === "not" ? "(должен быть 1)" : ""})
        </span>
        <div className="flex-1" />
        {(op !== "not" || children.length === 0) && (
          <PickerMenu onPick={addChild} />
        )}
        {onRemove && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            className="h-6 w-6"
          >
            <Trash2 className="w-3 h-3 text-destructive" />
          </Button>
        )}
      </div>

      {children.length === 0 ? (
        <div className="text-[10px] text-muted-foreground italic pl-2">
          Пусто. Добавьте child-узел.
        </div>
      ) : (
        children.map((child, idx) => (
          <NodeView
            key={idx}
            value={child}
            onChange={(next) => updateChild(idx, next)}
            depth={depth + 1}
            onRemove={() => removeChild(idx)}
          />
        ))
      )}
    </div>
  );
}

function LeafNode({
  type,
  value,
  onChange,
  onRemove,
}: {
  type: LeafType;
  value: Predicate;
  onChange: (next: Predicate) => void;
  onRemove?: () => void;
}): JSX.Element {
  const isArray = type === "product_in" || type === "category_in";

  const arrValue: number[] = isArray
    ? ((value[type] as unknown[]) ?? []).map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  const numValue: number = !isArray ? Number((value[type] as number) ?? 0) : 0;

  const label = LEAF_TYPES.find((lt) => lt.value === type)?.label ?? type;

  return (
    <div className="flex items-center gap-2 border rounded p-1.5 bg-background">
      <span className="text-xs font-medium w-44 shrink-0">{label}</span>
      {isArray ? (
        <Input
          value={arrValue.join(",")}
          onChange={(e) => {
            const ids = e.target.value
              .split(",")
              .map((s) => Number(s.trim()))
              .filter((n) => Number.isFinite(n) && n > 0);
            onChange({ [type]: ids });
          }}
          placeholder="1,2,3"
          className="h-7 text-xs font-mono"
        />
      ) : (
        <Input
          type="number"
          min={0}
          value={numValue}
          onChange={(e) => onChange({ [type]: Number(e.target.value) || 0 })}
          className="h-7 text-xs"
        />
      )}
      {type === "min_total" && (
        <span className="text-[10px] text-muted-foreground shrink-0">
          = {Math.round(numValue / 100).toLocaleString("ru-RU")} сум
        </span>
      )}
      {onRemove && (
        <Button size="icon" variant="ghost" onClick={onRemove} className="h-6 w-6">
          <Trash2 className="w-3 h-3 text-destructive" />
        </Button>
      )}
    </div>
  );
}
